/*
 * Class for executing append/selector changes.
 * @module
 */
var _ = require('optimizely-client-fn');

var actionTypes = require('core/constants/action_types');
var ApiEvents = require('core/api_events');
var constants = require('core/action/constants');
var createError = require('core/utils/custom_error').create;
var dataHelpers = require('core/action/element_data');
var deferred = require('core/utils/deferred');
var DirectiveStore = require('core/di').get('stores/directive');
var DocumentEnv = require('core/env/document');
var enums = require('core/action/enums');
var Flux = require('core/flux');
var Logger = require('core/utils/logger');
var RateMeter = require('core/utils/rate_meter');
var timingHelpers = require('core/action/timing_helpers');

var api = require('api/utils').create();

var CHANGE_APPLICATION_RATE = {
  MOVING_WINDOW_MILLISECONDS: __CHANGE_APPLICATION_RATE__.MOVING_WINDOW_MILLISECONDS,
  MAX_MACROTASKS_IN_MOVING_WINDOW: __CHANGE_APPLICATION_RATE__.MAX_MACROTASKS_IN_MOVING_WINDOW,
};


var ChangeOverheatError = createError('ChangeOverheatError');


/**
 * @constructor
 *
 * @param {Change} change
 * @param {ChangeConfig} config
 */
function AppendChange(change:Change, config:ChangeConfig) {
  this.change = change;
  this.identifier = config.identifier;
  this.startTime = config.startTime;

  if (DirectiveStore.shouldObserveChangesIndefinitely()) {
    Flux.dispatch(actionTypes.INITIALIZE_CHANGE_METRICS);

    this.rateMeter = new RateMeter(CHANGE_APPLICATION_RATE.MOVING_WINDOW_MILLISECONDS);

    // Set a rate limit on change applications.
    var maxRate = _.isNull(CHANGE_APPLICATION_RATE.MAX_MACROTASKS_IN_MOVING_WINDOW) ? Number.POSITIVE_INFINITY : CHANGE_APPLICATION_RATE.MAX_MACROTASKS_IN_MOVING_WINDOW;
    this.rateMeter.addListener(maxRate, _.bind(function() {
      Logger.warn('AppendChange', this, 'has overheated and will no longer apply or reapply');
      this.cancel();
      Flux.dispatch(actionTypes.RECORD_CHANGE_OVERHEATED);
      ApiEvents.emitError(new ChangeOverheatError('Change ' + this.identifier + ' has overheated'), {
        layerId: config.action && config.action.layerId,
        experimentId: config.action && config.action.experimentId,
        variationId: config.action && config.action.variationId,
        changeId: change.id,
        changeType: change.type,
        movingWindowMilliseconds: CHANGE_APPLICATION_RATE.MOVING_WINDOW_MILLISECONDS,
        maxMacroTasksInMovingWindow: maxRate,
      });
    }, this));

    // Track the temperature in RUM (as it reaches more and more milestones).
    var maxRateToTrackInRum = Math.min(maxRate, 50);
    for (var rate = 0; rate <= maxRateToTrackInRum; rate++) {
      this.rateMeter.addListener(rate, _.partial(function(closureRate) {
        Flux.dispatch(actionTypes.RECORD_CHANGE_MACROTASK_RATE, {
          changeMacrotaskRate: closureRate,
        });
      }, rate));
    }
  }
}

/**
 * Test the HTML string provided and return the number of root
 * HTML elements that it will create when inserted into the DOM.
 *
 * @param {string} htmlString - The string of HTML to check.
 * @return {number}
 */
AppendChange.prototype.numberOfRootNodes = function (htmlString:string): number {
  var testDiv = document.createElement('div');
  testDiv.innerHTML = htmlString;

  return testDiv.childNodes.length;
};

/**
 * Get the next n siblings or previous n siblings of the element provided.
 *
 * @param {Node} rootElement
 * @param {number} count
 * @param {boolean} iterateForward
 * @returns {Node[]}
 */
AppendChange.prototype.getSiblingElements = function(rootElement:Node, count:number, iterateForward:boolean): Node[] {
  var currentElement = rootElement;
  var list = [];

  for (var i = 0; i < count; i++) {
    if (iterateForward) {
      list.push(currentElement.nextSibling);
      currentElement = currentElement.nextSibling;
    } else {
      list.push(currentElement.previousSibling);
      currentElement = currentElement.previousSibling;
    }
  }
  return list;
};

/**
 * Setup the change to observe the given selector.
 */
AppendChange.prototype.apply = function () {
  this.applyDeferred = deferred();
  // If we have an invalid selector, document.querySelector and the api will throw an error
  try {
    var elementCount = this.numberOfRootNodes(this.change.value);
    if (!elementCount) {
      throw new Error('No DOM elements to be created for this change: ' + this.change.value);
    }

    var onTimeoutFn = _.partial(this.applyDeferred.reject, new Error('Unable to find selector.'));
    var observeSelectorOptions = {} as any;
    if (DirectiveStore.shouldObserveChangesUntilTimeout()) {
      observeSelectorOptions = {
        timeout: _.partial(timingHelpers.isTimedOut, this.startTime),
        onTimeout: onTimeoutFn
      };
    } else if (DirectiveStore.isEditor()) {
      // Continue to observe indefinitely and apply indefinitely, but ensure the appliedDeferred promise times out.
      // Else we'll freeze the editor if a matching element can't be found. (WEB-2512)
      api.waitUntil(_.partial(timingHelpers.isTimedOut, this.startTime))
        .then(onTimeoutFn, onTimeoutFn);
    } else {
      // Continue to observe indefinitely and apply indefinitely.
      // The appliedDeferred will remain pending (and therefore will block any dependent changes)
      // until its first application.
    }

    this.unobserveSelector = api.observeSelector(
      this.change.selector,
      _.bind(this.maybeApplyToElement, this),
      observeSelectorOptions
    );

    // If the selector's element exists now, apply the change immediately.
    // There is a short timeout on observeSelector that causes flickering in the editor
    // due to rapid apply/undo, so make sure to apply synchronously as well.
    var currentlyPresentNodes = DocumentEnv.querySelectorAll(this.change.selector);
    _.each(currentlyPresentNodes, _.bind(this.maybeApplyToElement, this));
  } catch(error) {
    this.applyDeferred.reject(error);
  }

  return this.applyDeferred;
};

/**
 * Apply the change to the individual element provided by observeSelector,
 * assuming we haven't already done so before.
 *
 * @param {HTMLElement} element - The element which triggered the change.
 */
AppendChange.prototype.maybeApplyToElement = function (element:HTMLElement) {
  var markingAttribute = constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id;

  // If this element has already been marked as an inserted node
  // for this changeId, its not eligible to trigger maybeApplyToElement again.
  // This prevents recursively applying the change based on the effects of this change,
  // so long as all inserted nodes have changeData attached to them.
  if (element.hasAttribute(markingAttribute)) {
    Logger.debug('Not applying AppendChange to element', element,
      'because it was inserted by this change');
    this.applyDeferred.resolve();
    return;
  }

  if (this.rateMeter) {
    this.rateMeter.countCurrentTick();
  }

  var applyOrReapplyToElement = _.bind(this.applyOrReapplyToElement, this, element, markingAttribute);
  applyOrReapplyToElement();

  this.applyDeferred.resolve();
};

/**
 * @param element
 * @param markingAttribute
 */
AppendChange.prototype.applyOrReapplyToElement = function(element, markingAttribute) {
  var operator;

  switch (this.change.operator) {
    case enums.DOMInsertionType.AFTER:
      operator = enums.insertAdjacentHTMLType.AFTER_END;
      break;
    case enums.DOMInsertionType.APPEND:
      operator = enums.insertAdjacentHTMLType.BEFORE_END;
      break;
    case enums.DOMInsertionType.BEFORE:
      operator = enums.insertAdjacentHTMLType.BEFORE_BEGIN;
      break;
    case enums.DOMInsertionType.PREPEND:
      operator = enums.insertAdjacentHTMLType.AFTER_BEGIN;
      break;
    default:
      operator = enums.insertAdjacentHTMLType.BEFORE_END;
  }

  // Add node to DOM (note this will work with text changes as well)
  // For operator types, see: https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentHTML
  element.insertAdjacentHTML(operator, this.change.value);

  // Mark the element identified by the change selector used to insert the HTML adjacent to
  // so we know that we've applied this append change using this element in the future.
  //
  // TODO: It's unclear if this serves any good purpose. It seems as though the only effect
  // of setting this attribute is to ensure that the selector-matching element is
  // highlighted, which may not be the UX we want; rather, we should strive to highlight
  // only inserted content. Remove if it's safe to do so.
  element.setAttribute(markingAttribute, '');

  // The selector element doesn't necessarily have any appended child nodes (e.g., in the "Before" position),
  // so initialize its list of appended child nodes to be empty
  dataHelpers.setData(element, this.change.id, this.identifier, []);

  // Based on the operator used for insertion, identify the inserted root element(s).
  var additionalSiblingCount = this.numberOfRootNodes(this.change.value) - 1;
  var nearestAppendedNode;
  var appendedNodeList;

  if (operator === enums.insertAdjacentHTMLType.BEFORE_END) {
    nearestAppendedNode = element.lastChild;
    appendedNodeList = this.getSiblingElements(nearestAppendedNode, additionalSiblingCount, false);
  } else if (operator === enums.insertAdjacentHTMLType.AFTER_BEGIN) {
    nearestAppendedNode = element.firstChild;
    appendedNodeList = this.getSiblingElements(nearestAppendedNode, additionalSiblingCount, true);
  } else if (operator === enums.insertAdjacentHTMLType.BEFORE_BEGIN) {
    nearestAppendedNode = element.previousSibling;
    appendedNodeList = this.getSiblingElements(nearestAppendedNode, additionalSiblingCount, false);
  } else if (operator === enums.insertAdjacentHTMLType.AFTER_END) {
    nearestAppendedNode = element.nextSibling;
    appendedNodeList = this.getSiblingElements(nearestAppendedNode, additionalSiblingCount, true);
  }
  appendedNodeList.unshift(nearestAppendedNode);

  // Mark the appended nodes (if they are element nodes; mark their parentElements if not)
  // so we can identify them with querySelector to highlight or undo,
  // as well as so we know to bail applying this change if any of inserted nodes triggers
  // this change recursively.
  _.each(appendedNodeList, _.bind(function(appendedNode) {
    // Attributes cannot be set on non-element nodes (e.g., text nodes),
    // so in those cases set the attribute on the parent element.
    // See https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
    var elementToMark = (appendedNode.nodeType === Node.ELEMENT_NODE)
      ? appendedNode
      : DocumentEnv.parentElement(appendedNode);
    elementToMark.setAttribute(markingAttribute, '');

    // Store a reference to the nodes so that we can remove them later if we want.
    var appendedNodes = dataHelpers.getData(elementToMark, this.change.id, this.identifier) || [];
    appendedNodes.push(appendedNode);
    dataHelpers.setData(elementToMark, this.change.id, this.identifier, appendedNodes);

    // Mark entire element tree rooted at elementToMark as having been inserted by this
    // change, to prevent any of the inserted elements triggering this change again
    _.each(DocumentEnv.childrenOf(elementToMark), function (e) {
      e.setAttribute(markingAttribute, '');
    });
  }, this));
};

/**
 * If this change is still being observed, cancel any future
 * invocations of it by invoking observeSelector's cancel function.
 */
AppendChange.prototype.cancel = function () {
  if (this.unobserveSelector) {
    this.unobserveSelector();
  }
};

if (__UNDO_CHANGES_UPON_VIEW_DEACTIVATION__ || __TEST__) {
  /**
   * Undo this change for all elements which have been marked for this changeId.
   *
   * This is done by leveraging the data stored via setData when applyOrReapplyToElement applied the change.
   */
  AppendChange.prototype.undo = function () {
    var markingAttribute = constants.CHANGE_ID_ATTRIBUTE_PREFIX + this.change.id;

    // NodeList<Element>; the set of inserted elements, plus the element the change's selector refers to
    var markedElements = DocumentEnv.querySelectorAll('[' + markingAttribute + ']');

    _.each(markedElements, _.bind(function removeAppendedChildNodes(baseElement) {
      // Remove from the DOM all the nodes inserted by way of `baseElement`
      // Recall that this could include baseElement itself!
      var appendedNodes = dataHelpers.getData(baseElement, this.change.id, this.identifier) || [];
      _.each(appendedNodes, function removeNode(node) {
        node.parentNode.removeChild(node);
      });

      // Remove from the base element both the marking attribute
      // and the list of references to the just-removed child nodes
      baseElement.removeAttribute(markingAttribute);
      dataHelpers.removeData(baseElement, this.change.id, this.identifier);

      // Clear the change data from the element's subtree subtree as well.
      // This could remove the data from nodes under this baseElement that
      // should /keep/ their data (e.g., if they were inserted by
      // another change), but in such cases the multiple changes are already
      // fighting to mark the baseElement with their respective change attribute
      // anyway, and that's currently unhandled anyway, so don't worry about this.
      _.each(DocumentEnv.childrenOf(baseElement), function (element) {
        element.removeAttribute(markingAttribute);
      });
    }, this));

    return deferred().resolve(enums.changeState.UNAPPLIED);
  };
}

/**
 * PluginManager registration method
 * @param {PluginManager} PluginManager
 */
module.exports = function(PluginManager:PluginManagerClass) {
  PluginManager.registerChangeApplier(enums.changeType.APPEND, AppendChange);
};

if (__TEST__) {
  module.exports.plugin = AppendChange;

  module.exports.ChangeOverheatError = ChangeOverheatError;

  module.exports.getChangeApplicationRate = function() {
    return CHANGE_APPLICATION_RATE;
  };

  module.exports.setChangeApplicationRate = function(changeApplicationRate) {
    CHANGE_APPLICATION_RATE = changeApplicationRate;
  };
}
